import { memo, useCallback } from 'react'

import { ControlPointDuplicate, AutoDeleteOutlined } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { VideoStream } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useVideoStreamCreateMutation, useVideoStreamDeleteMutation } from 'hooks/mutations/content'

const RowActionsRaw = ({ row, onClose, onClick }: RowActionsProps<VideoStream>) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createVideoStream, ...videoStreamCreateMutation } = useVideoStreamCreateMutation()
  const { mutate: deleteVideoStream } = useVideoStreamDeleteMutation()

  const handleDuplicateVideoStream = useCallback(() => {
    createVideoStream(
      { videoStreamInsert: row.original },
      {
        onSuccess: () => {
          onClose()
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [createVideoStream, enqueueSnackbar, onClose, row.original, t])

  const handleDeleteVideoStream = useCallback(() => {
    onClose()

    deleteVideoStream(
      { streamId: row.original.id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:Workspace.Video.nameDelete', { name: row.original.name }), { variant: 'success' }),
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteVideoStream, enqueueSnackbar, onClose, row.original.id, row.original.name, t])

  const handleLifecycleVideoStream = useCallback(() => {
    onClose()
    onClick?.(row)
  }, [onClick, onClose, row])

  return (
    <>
      <MenuItem onClick={handleDuplicateVideoStream} disabled={videoStreamCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {videoStreamCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItem onClick={handleLifecycleVideoStream}>
        <ListItemIcon>
          <AutoDeleteOutlined />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {t('domain:Workspace.Video.setContentLifecyclePolicy', { count: 1 })}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteVideoStream} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
